/**
 * Client wrapper for the Phase B training-plan API (`/api/training-plans`).
 * - `getTrainingPlanLibrary` — the wizard's one-shot bootstrap (distances, pace
 *   bands, plans + weekly ramp). Read-only, no CSRF.
 * - `applyTrainingPlan` — drop a periodized block + its race event, in one
 *   transaction. Anon sessions get the SAME durable event (anon-owned, merged
 *   onto the account at login), not a `null`.
 * - `removeTrainingPlan` — "Remove plan": delete a whole applied group.
 *
 * Mirrors `plannerApi.ts` (same `apiCall` / CSRF-refresh helpers).
 */
import { apiCall, apiCallWithCsrfRefresh } from "./api";
import { labelToEventDistance } from "./eventsApi";
import type { PlannedWorkout } from "./plannerTypes";
import type { FuelEstimatePolicy } from "@shared/modules/fuelPlans/fuelEstimate";

export type TrainingDistance = {
  id: "marathon" | "half" | "10k" | "custom";
  label: string;
  sub: string;
  ready: boolean;
};

/**
 * Resolve a race's free-form distance label to the plan library's distance id
 * (`marathon` | `half` | `10k` | `custom`) — i.e. which library the wizard loads
 * for a chosen race.
 *
 * Matching runs specific → lenient so a label can't be swallowed by a substring
 * collision. The classic trap: "Half marathon" *contains* "marathon", and
 * `marathon` sorts first in the catalog, so a naive `find(... includes ...)`
 * resolves a half to the marathon library. Order:
 *   1. Canonical resolver (shared with the Add-event picker): marathon/half/10k
 *      map straight through; any OTHER recognised distance (5K, ultra, tri,
 *      hyrox) is a no-plan distance → `custom` (never re-matched by substring).
 *   2. Free-form fallback for labels the canonical map doesn't know: the LONGEST
 *      catalog label the race label contains wins (most specific), so
 *      "Half Marathon Relay" → `half`, not `marathon`.
 *   3. Nothing matched → `custom`, the no-plan catch-all.
 */
export function resolveLibraryDistanceId(
  label: string,
  distances: TrainingDistance[]
): TrainingDistance["id"] {
  const canonical = labelToEventDistance(label);
  if (canonical === "marathon" || canonical === "half" || canonical === "10k") {
    return canonical;
  }
  if (canonical !== "other") return "custom"; // 5k / ultra / triathlon / hyrox

  const lc = label.trim().toLowerCase();
  let best: TrainingDistance | null = null;
  for (const d of distances) {
    const dl = d.label.toLowerCase();
    if ((dl === lc || lc.includes(dl)) && (!best || dl.length > best.label.length)) {
      best = d;
    }
  }
  return best?.id ?? "custom";
}

export type PaceBand = {
  finish: string;
  goalMinutes: number;
  goalPace: string;
  easyPace: string;
  longRunPace: string;
  marathonPace: string;
  tempo: string;
  interval: string;
  recoveryPace: string;
  maxWeeklyMiles: number;
};

export type PlanWeek = {
  week: number;
  phase: string;
  weeklyMileage: number;
  longRunMiles: number;
  flag: string | null;
  durationMinutes: number;
};

export type PlanSummary = {
  id: string;
  distance: string;
  weeks: number;
  finishTime: string;
  goalMinutes: number;
  status: string;
  stats: {
    runDaysPerWeek: number;
    peakWeeklyMiles: number;
    avgWeeklyMinutes: number;
  };
  weekly: PlanWeek[];
};

export type TrainingPlanLibrary = {
  distance: string;
  distances: TrainingDistance[];
  lengths: number[];
  paceBands: PaceBand[];
  plans: PlanSummary[];
  /** Optional for server/client deploy skew — estimate callers fall back. */
  fuelEstimatePolicy?: FuelEstimatePolicy | null;
};

export type AppliedPlanEvent = {
  id: string;
  name: string;
  eventDate: string;
  sport: string | null;
  distanceMeters: number | null;
  distanceLabel: string | null;
};

export type ApplyTrainingPlanResult = {
  planGroupId: string;
  created: PlannedWorkout[];
  /** The race event the apply created (or reused) under the caller's principal —
   *  customer OR anon session (anon-owned rows re-point onto the account at
   *  login). Nullable to match the server contract's pre-anon-ownable shape. */
  event: AppliedPlanEvent | null;
};

const BASE = "/api/training-plans";

export async function getTrainingPlanLibrary(
  distance = "marathon",
  signal?: AbortSignal
): Promise<TrainingPlanLibrary> {
  return apiCall<TrainingPlanLibrary>(BASE, { query: { distance }, signal });
}

export type ResolvePlanResult = {
  planId: string;
  finishTime: string;
  goalMinutes: number;
  listed: boolean;
  /** true = an exact by-minute plan matched; false = fell back to nearest listed. */
  exact: boolean;
  band: PaceBand | null;
};

/**
 * Resolve a manual goal time (whole minutes) to a concrete plan id. Reaches the
 * off-grid by-minute plans on an exact match and otherwise falls back to the
 * nearest listed plan. Read-only, no CSRF — mirrors the bootstrap.
 */
export async function resolveTrainingPlan(
  body: { distance: string; weeks: number; minutes: number },
  signal?: AbortSignal
): Promise<ResolvePlanResult> {
  return apiCall<ResolvePlanResult>(`${BASE}/resolve`, { method: "POST", body, signal });
}

export async function applyTrainingPlan(
  body: {
    planId: string;
    raceDate: string;
    raceName?: string;
    eventCatalogId?: string;
    distanceLabel?: string;
    customerEventId?: string;
  },
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<ApplyTrainingPlanResult> {
  return apiCallWithCsrfRefresh<ApplyTrainingPlanResult>(
    `${BASE}/apply`,
    { method: "POST", body, csrfToken },
    refreshCsrf
  );
}

export async function removeTrainingPlan(
  planGroupId: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<number> {
  const res = await apiCallWithCsrfRefresh<{ deleted: number }>(
    `${BASE}/plan-group/${encodeURIComponent(planGroupId)}`,
    { method: "DELETE", csrfToken },
    refreshCsrf
  );
  return res.deleted;
}

/**
 * Link the race event onto an applied plan's workouts and/or a merged `build`
 * journey — the anon→login replay backfill. Called once after a guest signs in
 * and their held race becomes a real `CustomerEvent`. At least one of
 * `planGroupId`/`journeyId` is required (400 otherwise); `linked` sums both
 * paths (0 if the target isn't the caller's or is already linked). Idempotent
 * server-side.
 */
export async function linkTrainingPlanEvent(
  target: {
    planGroupId?: string;
    journeyId?: string;
    customerEventId: string;
    intent?: "goal" | "milestone";
  },
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<number> {
  const res = await apiCallWithCsrfRefresh<{ linked: number }>(
    `${BASE}/link-event`,
    { method: "POST", body: target, csrfToken },
    refreshCsrf
  );
  return res.linked;
}
